import { authActions } from '../../../core/auth/auth.actions'
import moment from 'moment-mini'
import { combineLatest, firstValueFrom, interval, Observable, of } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  tap
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { environment } from 'src/environments/environment'
import { Component, inject, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Store } from '@ngrx/store'
import { bookingSelectors } from '../../booking/booking.selectors'
import { ICalendarService } from '../../booking/icalendar/icalendar.service'
import { configurationOptionSelectors } from '../../configuration-option/configuration-option.selectors'
import { courseSelectors } from '../../course/course.selectors'
import { operationActions } from '../../operation/operation.actions'
import { productSelectors } from '../../product/product.selectors'
import { CollecStatusType, Payment, PayMethodType } from '../payment/payment'
import { paymentActions } from '../payment/payment.actions'
import { Sale, StatusCode } from '../sale'
import { SaleItem } from '../sale-item/sale-item'
import { saleItemActions } from '../sale-item/sale-item.actions'
import { saleItemSelectors } from '../sale-item/sale-item.selectors'
import { SaleService } from '../sale.service'
import { BsModalRef } from 'ngx-bootstrap/modal'
import { authSelectors } from 'src/app/core/auth/auth.selectors'
import { Member } from '../../member/member'
import { ConfigurationService } from 'src/app/shared/services/configuration.service'
import { participationActions } from '../../booking/participation/participation.actions'
import { PaymentService } from '../payment/payment.service'
import { subscriberActions } from '../../subscription/subscriber/subscriber.actions'
import { rentalActions } from '../../rental/rental.actions'
import { Document } from '../../document/document'
import { locationActions } from '../../location/location.actions'
import { BreadCrumbActions } from 'src/app/shared/components/breadcrumb/breadcrumb.actions'
import { AppRoutesNS } from 'src/app/app-routes.namespace'
import { FeedbackService } from 'src/app/shared/components/feedback-modal/feedback.service'
import { ConfigOptionsKeys } from 'src/app/shared/constants'
import { BookingTerms } from '../../product/product'
import { userRoleSelectors } from '../../role/user-role/user-role.selectors'
import { RolesType } from '../../role/role'
import { cartItemActions } from '../../cart/cart-item/cart-item.actions'
import { HomePageConfigWebOption } from '../../configuration-option/configuration-option'
import { QueryClient } from '@tanstack/angular-query-experimental'
import { bookingQueryKeys } from '../../booking/booking.query-keys'
import { normalizationActions } from 'src/app/shared/normalization/normalization.actions'
import { normalizeToSchema } from 'src/app/shared/normalization/normalization'
import { answerActions } from '../../survey/answer/answer.actions'

export interface SaleCallbackRouteParams {
  status: 'success' | 'failure'
  paymentReference: string
  amount: string
  paymentMethod: PayMethodType
}
@UntilDestroy()
@Component({
  selector: 'sale-callback-page',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class SaleCallbackComponent implements OnInit, OnDestroy {
  private readonly queryClient = inject(QueryClient)

  public params: SaleCallbackRouteParams
  public saleItems$: Observable<SaleItem[]>
  public saleItemsTotal$: Observable<string>

  public SaleStatusCode = StatusCode
  public PayMethodType = PayMethodType
  public bookingModuleConfigs = environment.moduleConfigs.bookingModule
  public shopModuleConfigs = environment.moduleConfigs.shopModule
  public showAddToCalendarButton: boolean = false

  public sale$: Observable<Sale>
  public saleRequiresApproval$: Observable<boolean>
  public selectedLanguage$: Observable<string>
  public orderCompletedTitle$: Observable<string>
  public orderCompletedSubtitle$: Observable<string>
  public staticAssetBaseUrl: string = environment.staticAssetBaseUrl
  public primaryLogoUrl$: Observable<string>
  public websiteUrl$: Observable<string>
  public confirmationMailTemplate$: Observable<string>
  public bsModalRef: BsModalRef
  public member: Member
  public sale: Sale
  public payments: Payment[]
  public homeNavigationTitle$: Observable<string>
  public saleDocuments: Document[]
  public showDownloadTicketsButton = environment.showDownloadTicketsButton
  public activeTicketingBlockModule =
    environment.moduleConfigs?.shopModule?.shopIndex?.ticketingBlock ?? false
  public memberIsGuest$: Observable<boolean>
  public appRoutesNS = AppRoutesNS
  private didSideFetchForSaleItems: boolean = false
  private shouldLogoutGuestOnDestroy: boolean = true
  constructor(
    private store: Store<AppState>,
    public configurationService: ConfigurationService,
    private icalendarService: ICalendarService,
    public saleService: SaleService,
    private router: Router,
    private paymentService: PaymentService,
    private route: ActivatedRoute,
    private feedbackService: FeedbackService
  ) {}

  async ngOnInit() {
    this.store.dispatch(
      BreadCrumbActions.addPageToHistory({
        pageName: 'saleModule.yourOrder',
        url: environment.moduleConfigs.authModule.afterLoginSuccessRedirect,
        showBackArrow: false,
        titleName: 'saleModule.yourOrder'
      })
    )

    this.member = await firstValueFrom(
      this.store
        .select(authSelectors.selectMember)
        .pipe(filter((member) => Boolean(member)))
    )

    if (!this.member.id) {
      return
    }

    this.store.dispatch(paymentActions.resetState())
    this.store.dispatch(locationActions.fetchManyPublic({}))
    this.memberIsGuest$ = this.store.select(authSelectors.selectMemberIsGuest)

    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )
    this.confirmationMailTemplate$ = this.store
      .select(
        configurationOptionSelectors.selectByKey,
        'template_sale_mail_confirmation'
      )
      .pipe(
        filter((x) => Boolean(x)),
        map((option) => option.value)
      )
    this.primaryLogoUrl$ = this.store
      .select(
        configurationOptionSelectors.selectByKey,
        'primary_logo_image_path'
      )
      .pipe(
        filter((x) => Boolean(x)),
        map((configOption) =>
          configOption?.value.length
            ? `${environment.staticAssetBaseUrl}${configOption?.value}`
            : ''
        )
      )
    this.websiteUrl$ = this.selectedLanguage$.pipe(
      switchMap((language) => {
        return this.store.select(
          configurationOptionSelectors.selectByKey,
          `website_url`
        )
      }),
      filter((configurationOption) => Boolean(configurationOption)),
      map((configurationOption) => configurationOption.value)
    )
    this.orderCompletedTitle$ = combineLatest([
      this.store.select(
        configurationOptionSelectors.selectByKey,
        'order_completed_text'
      ),
      this.selectedLanguage$
    ]).pipe(
      filter(([configurationOption, _]) => Boolean(configurationOption)),
      map(
        ([x, selectedLanguage]) =>
          JSON.parse(x?.value)[`order_completed_title_${selectedLanguage}`]
      )
    )

    this.orderCompletedSubtitle$ = combineLatest([
      this.store.select(
        configurationOptionSelectors.selectByKey,
        'order_completed_text'
      ),
      this.selectedLanguage$
    ]).pipe(
      filter(([configurationOption, _]) => Boolean(configurationOption)),
      map(
        ([x, selectedLanguage]) =>
          JSON.parse(x?.value)[`order_completed_subtitle_${selectedLanguage}`]
      )
    )

    this.homeNavigationTitle$ = combineLatest([
      this.store.select(
        configurationOptionSelectors.selectByKeyWithType(
          ConfigOptionsKeys.HOME_PAGE_CONFIG
        )
      ),
      this.selectedLanguage$
    ]).pipe(
      map(
        ([homePageConfig, selectedLanguage]) =>
          homePageConfig?.title?.[
            selectedLanguage as keyof HomePageConfigWebOption['title']
          ] ?? 'general.home'
      )
    )

    this.params = {
      ...this.route.snapshot.queryParams
    } as SaleCallbackRouteParams

    if (!this.params.paymentReference) {
      this.params.paymentReference =
        this.route.snapshot.queryParams.transactionid ??
        this.route.snapshot.queryParams.omschrijving
    }
    if (!this.params.paymentReference?.length) {
      this.router.navigate([AppRoutesNS.Upcoming.UPCOMING_BOOKINGS])
      return
    }

    const response = await firstValueFrom(
      this.paymentService.getMany(
        RequestQueryBuilder.create({
          search: {
            reference: this.params.paymentReference,
            memberId: this.member.id
          },
          join: [{ field: 'sale' }, { field: 'sale.payments' }]
        })
      )
    )

    if (response.data?.length === 1) {
      const paymentForReference = response.data[0] // there should only be 1 payment for the reference
      this.sale = paymentForReference.sale
    } else {
      console.warn(
        'Expected exactly one payment for reference ' +
          this.params.paymentReference +
          ' and will fall back to sale lookup',
        response.data
      )

      this.sale = await this.getSaleFromPaymentReference()
      if (!this.sale?.id) {
        console.error(
          'Could not resolve sale from payment reference ' +
            this.params.paymentReference
        )
        return
      }
    }

    this.store.dispatch(
      normalizationActions.upsertMany({
        entities: normalizeToSchema({ sales: [this.sale] })
      })
    )

    this.payments = this.sale.payments
      ?.filter(
        (p) => ![PayMethodType.PAY_METHOD_AGGREGATE].includes(p.payMethod)
      )
      .sort((a, b) => {
        const aCollected =
          a.collectStatus === CollecStatusType.COLLECT_STATUS_COLLECTED
        const bCollected =
          b.collectStatus === CollecStatusType.COLLECT_STATUS_COLLECTED

        if (aCollected !== bCollected) return aCollected ? -1 : 1
        return moment(b.date).valueOf() - moment(a.date).valueOf()
      })

    const referencedPayment = this.payments.find(
      (p) => p.reference === this.params.paymentReference
    )

    this.sale$ = of(this.sale) // should be done later when we drop more observables can go tanstack
    if (this.sale.statusCode == StatusCode.DONE) {
      this.onSaleResolved()
    } else if (
      this.params.status === 'failure' ||
      referencedPayment?.collectStatus ===
        CollecStatusType.COLLECT_STATUS_CANCEL
    ) {
      this.transitionToFailureState()
    } else {
      this.startSaleStatusPolling(this.sale)
    }

    this.store.dispatch(
      saleItemActions.fetchManyBySaleId({
        saleId: this.sale.id,
        params: RequestQueryBuilder.create({
          join: [
            { field: 'sale' },
            { field: 'booking' },
            { field: 'booking.product' },
            { field: 'booking.product.translations' },
            { field: 'booking.participations' },
            { field: 'product' },
            { field: 'product.linkedSubscriptions' },
            { field: 'product.translations' },
            { field: 'product.documents' },
            { field: 'product.location' },
            { field: 'course' },
            // { field: 'course.bookings' }, // todo find solution with better preformance
            { field: 'course.translations' },
            { field: 'subscription' },
            { field: 'subscription.translations' },
            { field: 'rental' }
          ]
        })
      })
    )

    this.saleItems$ = this.store
      .select(saleItemSelectors.selectByFilter, {
        field: 'saleId',
        value: this.sale.id
      })
      .pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)),
        map((saleItems) => saleItems.sort((a, b) => a.bookingId - b.bookingId)),
        tap((saleItems) => this.doSaleItemSideEffects(saleItems))
      )

    this.saleItemsTotal$ = this.store.select(
      saleItemSelectors.selectTotal,
      this.sale.id
    )

    this.saleRequiresApproval$ = combineLatest([
      this.store.select(productSelectors.selectEntities),
      this.saleItems$,
      this.store.select(userRoleSelectors.selectUserHasRoles, [RolesType.ADMIN])
    ]).pipe(
      map(([productEntities, saleItems, isAdmin]) => {
        if (isAdmin) {
          // admins get their things preapproved
          return false
        }

        for (let saleItem of saleItems) {
          if (
            productEntities[saleItem.productId]?.bookingTerms ==
            BookingTerms.APPROVAL_BASED
          ) {
            return true
          }
        }

        return false
      })
    )
  }

  private onSaleResolved() {
    this.queryClient.invalidateQueries({
      queryKey: bookingQueryKeys.all
    })

    this.store.dispatch(
      answerActions.submitPendingSaleSurveyAnswers({ saleId: this.sale.id })
    )

    this.store.dispatch(cartItemActions.resetState())
    this.feedbackService.triggerFeedbackAfterSuccessfulAction()

    this.store.dispatch(
      operationActions.fetchMyOperations({
        params: RequestQueryBuilder.create({
          search: {
            isPending: true
          },
          join: [{ field: 'survey' }, { field: 'survey.questions' }]
        }),
        // the sale can close pending operations (e.g. required subscription purchase)
        pendingOnly: true
      })
    )

    this.store.dispatch(
      subscriberActions.fetchManyByMemberId({
        memberId: this.member.id,
        force: true
      })
    )

    if (this.showDownloadTicketsButton) {
      this.pollForSaleDocuments(this.sale)
    } else {
      this.logoutGuestUsers()
    }
  }

  private transitionToFailureState() {
    this.saleService.resetHostedPagesLocalStorage()
    this.params.status = 'failure'
  }

  private startSaleStatusPolling(sale: Sale) {
    const saleFetcherIntervalSubscription = interval(2000).subscribe(
      async (intervalCount) => {
        const fetchedSale = await firstValueFrom(
          this.saleService.getOne(
            sale.id,
            RequestQueryBuilder.create({
              search: {
                id: sale.id,
                customerPersonId: this.member.id
              },
              join: [{ field: 'payments' }]
            })
          )
        )

        this.payments = fetchedSale.payments
        const referencedPayment = this.payments.find(
          (p) => p.reference === this.params.paymentReference
        )

        this.sale = fetchedSale

        this.store.dispatch(
          normalizationActions.upsertMany({
            entities: normalizeToSchema({ sales: [this.sale] })
          })
        )

        if (fetchedSale.statusCode == StatusCode.DONE) {
          this.onSaleResolved()
          saleFetcherIntervalSubscription.unsubscribe()
        } else if (
          referencedPayment &&
          referencedPayment.collectStatus ===
            CollecStatusType.COLLECT_STATUS_CANCEL
        ) {
          saleFetcherIntervalSubscription.unsubscribe()
          this.params.status = 'failure'
          this.transitionToFailureState()
        } else if (intervalCount > 150) {
          // stop polling after 5 minutes
          saleFetcherIntervalSubscription.unsubscribe()
          this.params.status = 'failure'
        }
      }
    )
  }

  private async getSaleFromPaymentReference(): Promise<Sale> {
    const saleId = Number(this.params.paymentReference?.split('-')?.[0])
    if (!saleId) {
      return null
    }

    return firstValueFrom(
      this.saleService.getOne(
        saleId,
        RequestQueryBuilder.create({
          search: {
            id: saleId,
            customerPersonId: this.member.id
          },
          join: [{ field: 'payments' }]
        })
      )
    )
  }

  private pollForSaleDocuments(sale: Sale) {
    this.saleService
      .isTicketSale$(sale)
      .pipe(
        filter((isTicket) => isTicket),
        take(1),
        untilDestroyed(this)
      )
      .subscribe((_isTicketSale) => {
        const intervalSubscription = interval(1000)
          .pipe(untilDestroyed(this))
          .subscribe(async (_interval) => {
            const fetchedSale = await firstValueFrom(
              this.saleService.getOne(
                sale.id,
                RequestQueryBuilder.create({
                  search: {
                    id: sale.id,
                    customerPersonId: this.member.id
                  },
                  join: [{ field: 'documents' }]
                })
              )
            )

            console.log(
              'Polling for sale documents, got sale with documents: ',
              fetchedSale.documents
            )

            if (fetchedSale.documents?.length) {
              this.saleDocuments = fetchedSale.documents
              intervalSubscription.unsubscribe()
              this.logoutGuestUsers()
            }
          })
      })
  }

  private doSaleItemSideEffects(saleItems: SaleItem[]) {
    if (!saleItems?.length || this.didSideFetchForSaleItems) {
      return
    }

    this.didSideFetchForSaleItems = true
    // fetch rental to show the rental period
    this.store.dispatch(
      rentalActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            saleItemId: {
              [CondOperator.IN]: saleItems.map((saleItem) => saleItem.id)
            }
          }
        })
      })
    )

    if (
      saleItems.some((saleItem) => saleItem.bookingId) ||
      saleItems.some((saleItem) => saleItem.courseId)
    ) {
      this.showAddToCalendarButton = true

      if (saleItems.some((saleItem) => saleItem.bookingId)) {
        const bookingIds: any = [
          ...new Set(
            saleItems
              .map((saleItem) => saleItem.bookingId)
              .filter((bookingId) => Boolean(bookingId))
          )
        ]

        if (bookingIds) {
          this.store.dispatch(
            participationActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: {
                  memberId: this.member.id,
                  bookingId: { [CondOperator.IN]: bookingIds }
                }
              })
            })
          )
        }
      }
    }
  }

  public onClickDownloadTicket(): void {
    if (!this.saleDocuments?.length) {
      console.log(
        'onClickDownloadTicket got no documents loaded and will stop will use these'
      )
      return
    }

    console.log(
      'onClickDownloadTicket got preloaded documents and will use these',
      this.saleDocuments
    )

    for (const document of this.saleDocuments) {
      console.log(`Running on document ${JSON.stringify(document)}`)

      if (document.publicUrl?.length) {
        window.open(document.publicUrl, '_blank').focus()
      }
    }
  }

  public getIcalDownload() {
    combineLatest([
      this.saleItems$,
      this.store.select(productSelectors.selectEntities),
      this.store.select(courseSelectors.selectEntities),
      this.store.select(bookingSelectors.selectEntities)
    ])
      .pipe(take(1))
      .subscribe(
        ([saleItems, productEntities, courseEntities, bookingEntities]) => {
          saleItems.forEach((saleItem) => {
            if (
              saleItem.productId &&
              saleItem.params &&
              saleItem.params.startDate
            ) {
              const bookableLinkedProduct = productEntities[saleItem.productId]
              const bookableProduct =
                productEntities[saleItem.params.bookableProductId]

              this.icalendarService.getIcaldownloadForProduct(
                bookableLinkedProduct,
                bookableProduct,
                moment(saleItem.params.startDate),
                moment(saleItem.params.endDate)
              )
            } else if (saleItem.courseId) {
              const course = courseEntities[saleItem.courseId]
              const bookings = []
              for (let bookingId of course.bookings) {
                bookings.push(bookingEntities[bookingId])
              }

              this.icalendarService.getIcaldownloadForCourse(
                bookings,
                course.id
              )
            }
          })
        }
      )
  }

  public onClickSchedule() {
    this.router.navigateByUrl(AppRoutesNS.Home.HOME)
  }

  public onClickSaleDetailButton() {
    this.router.navigateByUrl('/sales/' + this.sale.id)
  }

  public onClickRetryPayment() {
    this.shouldLogoutGuestOnDestroy = false
    this.router.navigate(['/sales', this.sale.id, 'checkout'])
  }

  private logoutGuestUsers() {
    setTimeout(() => {
      this.store.dispatch(authActions.logoutIfGuest({ redirectToShop: false }))
    }, 2500) // delay signout by 2,5 seconds
  }

  ngOnDestroy() {
    if (this.shouldLogoutGuestOnDestroy) {
      this.store.dispatch(authActions.logoutIfGuest({ redirectToShop: false }))
    }
  }
}
